import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { createWatermarkedImage } from '../services/imageUtils';
import {
  clearPendingGooglePhotosSave,
  peekPendingGooglePhotosSave,
} from '../services/googlePhotosExport';
import { GooglePhotosSaveButton } from './GooglePhotosSaveButton';

type ImportedImage = {
  fileName: string;
  mimeType: string;
  base64Data: string;
  dataUrl: string;
};

type Props = {
  onImport?: (image: ImportedImage) => void | Promise<void>;
};

export const GooglePhotosPanel: React.FC<Props> = ({ onImport }) => {
  const { token } = useAuth();
  const [params, setSearchParams] = useSearchParams();
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [waitingForPicker, setWaitingForPicker] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pickerWindowRef = useRef<Window | null>(null);
  const cancelledRef = useRef(false);

  const clearPoll = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const refreshStatus = useCallback(async () => {
    try {
      const status = await api.googlePhotosStatus(token);
      setConnected(Boolean(status.connected));
      setConfigured(status.configured !== false);
      if (status.message && !status.connected) setMessage(status.message);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not check Google Photos status');
    }
  }, [token]);

  useEffect(() => {
    refreshStatus();
    return () => {
      cancelledRef.current = true;
      clearPoll();
    };
  }, [refreshStatus]);

  useEffect(() => {
    const code = params.get('code');
    if (!code) return;

    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const data = await api.googlePhotosCallback(code, token);
        if (cancelled) return;
        if ((data as { error?: string }).error) {
          setMessage((data as { error: string }).error);
        } else {
          setConnected(true);
          const pendingSave = peekPendingGooglePhotosSave();
          if (pendingSave) {
            setMessage(
              'Google Photos connected. Return to your restoration and click Save to Google Photos.'
            );
            clearPendingGooglePhotosSave();
          } else {
            setMessage('Google Photos connected. Click Import to choose photos.');
          }
          const next = new URLSearchParams(params);
          next.delete('code');
          next.delete('state');
          next.delete('scope');
          setSearchParams(next, { replace: true });
        }
      } catch (e) {
        if (!cancelled) setMessage(e instanceof Error ? e.message : 'OAuth callback failed');
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [params, token, setSearchParams]);

  const connect = async () => {
    try {
      const { url } = await api.googlePhotosAuthUrl(token);
      window.location.href = url;
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not start Google Photos OAuth');
    }
  };

  const finishImport = async (sessionId: string) => {
    setMessage('Importing selected photos…');
    const result = await api.googlePhotosPickerImport(sessionId, token);
    const images = result.images || [];
    for (const image of images) {
      await onImport?.(image);
    }
    setMessage(
      images.length
        ? `Imported ${images.length} photo${images.length === 1 ? '' : 's'} from Google Photos.`
        : 'No photos were selected.'
    );
  };

  const pollSession = (sessionId: string, pollIntervalMs: number, deadline: number) => {
    clearPoll();
    const tick = async () => {
      if (cancelledRef.current) return;
      if (Date.now() > deadline) {
        setWaitingForPicker(false);
        setBusy(false);
        setMessage('Google Photos selection timed out. Try again.');
        try {
          await api.googlePhotosPickerDelete(sessionId, token);
        } catch {
          /* ignore */
        }
        return;
      }

      try {
        const session = await api.googlePhotosPickerSession(sessionId, token);
        if (session.mediaItemsSet) {
          setWaitingForPicker(false);
          try {
            await finishImport(sessionId);
          } catch (err) {
            setMessage(err instanceof Error ? err.message : 'Import failed');
          } finally {
            setBusy(false);
            try {
              pickerWindowRef.current?.close();
            } catch {
              /* ignore */
            }
          }
          return;
        }

        const nextInterval =
          session.pollingConfig?.pollIntervalMs && session.pollingConfig.pollIntervalMs > 0
            ? session.pollingConfig.pollIntervalMs
            : pollIntervalMs;
        const nextTimeout = session.pollingConfig?.timeoutInMs;
        if (nextTimeout === 0) {
          setWaitingForPicker(false);
          setBusy(false);
          setMessage('Google Photos selection ended. Try again.');
          return;
        }
        pollTimerRef.current = setTimeout(tick, nextInterval);
      } catch (err) {
        setWaitingForPicker(false);
        setBusy(false);
        setMessage(err instanceof Error ? err.message : 'Picker polling failed');
      }
    };

    pollTimerRef.current = setTimeout(tick, pollIntervalMs);
  };

  const startPicker = async () => {
    cancelledRef.current = false;
    setBusy(true);
    setMessage(null);
    try {
      if (!connected) {
        await connect();
        return;
      }

      const session = await api.googlePhotosPickerCreate({ maxItemCount: 20 }, token);
      if (session.mock) {
        setMessage(session.message || 'Google Photos is not configured.');
        setBusy(false);
        return;
      }
      if (!session.sessionId || !session.pickerUri) {
        throw new Error('Picker session did not return a URI');
      }

      setWaitingForPicker(true);
      setMessage('Waiting for photo selection…');
      pickerWindowRef.current = window.open(session.pickerUri, 'kinvera-gphotos-picker', 'noopener,noreferrer');

      const interval = session.pollingConfig?.pollIntervalMs || 3000;
      const timeout = session.pollingConfig?.timeoutInMs || 5 * 60 * 1000;
      pollSession(session.sessionId, interval, Date.now() + timeout);
    } catch (err) {
      setWaitingForPicker(false);
      setBusy(false);
      const msg = err instanceof Error ? err.message : 'Could not open Google Photos Picker';
      if (/not connected|Authorize first|Reconnect/i.test(msg)) {
        setConnected(false);
        setMessage('Reconnect Google Photos to continue.');
      } else {
        setMessage(msg);
      }
    }
  };

  const cancelPicker = async () => {
    cancelledRef.current = true;
    clearPoll();
    setWaitingForPicker(false);
    setBusy(false);
    setMessage('Selection cancelled.');
    try {
      pickerWindowRef.current?.close();
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="kin-gphotos">
      <div className="kin-gphotos-head">
        <h3>Google Photos</h3>
        <div className="kin-cta-row">
          {!connected ? (
            <button type="button" className="kin-btn kin-btn-ghost" onClick={connect} disabled={busy || !configured}>
              Connect
            </button>
          ) : (
            <button
              type="button"
              className="kin-btn kin-btn-ghost"
              onClick={async () => {
                try {
                  await api.googlePhotosDisconnect(token);
                  setConnected(false);
                  setMessage('Google Photos disconnected.');
                } catch (err) {
                  setMessage(err instanceof Error ? err.message : 'Disconnect failed');
                }
              }}
              disabled={busy}
            >
              Disconnect
            </button>
          )}
          <button
            type="button"
            className="kin-btn kin-btn-primary"
            onClick={startPicker}
            disabled={busy || !configured}
          >
            {connected ? 'Import from Google Photos' : 'Connect & import'}
          </button>
          {waitingForPicker && (
            <button type="button" className="kin-btn kin-btn-ghost" onClick={cancelPicker}>
              Cancel
            </button>
          )}
        </div>
      </div>
      {message && <p className="kin-banner">{message}</p>}
      {waitingForPicker && (
        <p className="kin-muted" style={{ marginTop: '0.75rem' }}>
          Complete your selection in the Google Photos window. Kinvera will import photos when you finish.
        </p>
      )}
    </div>
  );
};

export const ShareActions: React.FC<{
  imageUrl?: string | null;
  fileName?: string;
  watermarkExportsEnabled?: boolean;
}> = ({ imageUrl, fileName, watermarkExportsEnabled = true }) => {
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!imageUrl) return null;

  const resolveExportUrl = async () => {
    if (!watermarkExportsEnabled) return imageUrl;
    return createWatermarkedImage(imageUrl);
  };

  const share = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const exportUrl = await resolveExportUrl();
      const blob = await (await fetch(exportUrl)).blob();
      const file = new File([blob], fileName || 'kinvera-restored.png', { type: blob.type });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Restored with Kinvera',
          text: 'Restored with Kinvera',
        });
      } else {
        const a = document.createElement('a');
        a.href = exportUrl;
        a.download = fileName || 'kinvera-restored.png';
        a.click();
        setMsg('Download started — share from your device gallery.');
      }
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Share failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kin-share flex flex-wrap items-center gap-2">
      <button type="button" className="kin-btn kin-btn-ghost" onClick={share} disabled={busy}>
        Share
      </button>
      <GooglePhotosSaveButton
        variant="share"
        imageUrl={imageUrl}
        fileName={fileName}
        watermarkExportsEnabled={watermarkExportsEnabled}
      />
      {msg && <span className="kin-muted">{msg}</span>}
    </div>
  );
};
